import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { RefreshCw, Loader2, AlertTriangle, Info, CheckCircle, DollarSign, TrendingUp } from 'lucide-react';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => `${(v * 100).toFixed(1)}%`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-800 p-3 rounded-lg shadow-xl">
      <p className="text-xs text-zinc-400 mb-1 font-mono">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400">{p.name}:</span>
          <span className="font-mono text-zinc-200">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function DCFValuationPage({ data, results, analysis, saveAnalysis, loading }) {
  const dcf = results?.dcf || {};
  const assumptions = data?.dcf_assumptions || {};
  const sdeAnalysis = results?.sde_analysis || [];

  // Get recalculated SDE (2-Year Average from P&L analysis)
  const recalculatedSDE = sdeAnalysis.find(s => s.label?.includes('Average'))?.sde 
    || sdeAnalysis[sdeAnalysis.length - 1]?.sde 
    || 0;

  // Get broker SDE (from seller deal tab)
  const brokerSDE = data?.seller_deal?.sde || 0;

  // Calculate variance between broker SDE and recalculated SDE
  const sdeVariance = recalculatedSDE > 0 
    ? Math.abs((brokerSDE - recalculatedSDE) / recalculatedSDE * 100) 
    : 0;
  const showSDEWarning = sdeVariance > 15 && brokerSDE > 0 && recalculatedSDE > 0;

  const [inputs, setInputs] = useState({
    discount_rate: assumptions.discount_rate || 0.15,
    growth_rate: assumptions.growth_rates?.[0] || 0.03,
    exit_multiple: assumptions.exit_multiple || 3.0,
    cogs_pct: assumptions.cogs_pct || 0.6041,
    opex_pct: assumptions.opex_pct || 0.2263,
    buyer_salary: assumptions.buyer_salary || 75000,
    tax_rate: assumptions.tax_rate || 0,
    capex_pct: assumptions.capex_pct || 0.025,
    working_capital_pct: assumptions.working_capital_pct || 0.10,
  });

  useEffect(() => {
    if (assumptions.discount_rate) {
      setInputs({
        discount_rate: assumptions.discount_rate || 0.15,
        growth_rate: assumptions.growth_rates?.[0] || 0.03,
        exit_multiple: assumptions.exit_multiple || 3.0,
        cogs_pct: assumptions.cogs_pct || 0.6041,
        opex_pct: assumptions.opex_pct || 0.2263,
        buyer_salary: assumptions.buyer_salary || 75000,
        tax_rate: assumptions.tax_rate || 0,
        capex_pct: assumptions.capex_pct || 0.025,
        working_capital_pct: assumptions.working_capital_pct || 0.10,
      });
    }
  }, [assumptions.discount_rate, assumptions.growth_rates, assumptions.exit_multiple, assumptions.cogs_pct, assumptions.opex_pct, assumptions.buyer_salary, assumptions.tax_rate, assumptions.capex_pct, assumptions.working_capital_pct]);

  const handleRecalculate = useCallback(async () => {
    if (!analysis?.id) return;
    const updatedAssumptions = {
      ...assumptions,
      discount_rate: inputs.discount_rate,
      growth_rates: [inputs.growth_rate, inputs.growth_rate, inputs.growth_rate, inputs.growth_rate, inputs.growth_rate],
      exit_multiple: inputs.exit_multiple,
      cogs_pct: inputs.cogs_pct,
      opex_pct: inputs.opex_pct,
      buyer_salary: inputs.buyer_salary,
      tax_rate: inputs.tax_rate,
      capex_pct: inputs.capex_pct,
      working_capital_pct: inputs.working_capital_pct,
    };
    await saveAnalysis({
      ...data,
      name: analysis.name || 'Analysis',
      dcf_assumptions: updatedAssumptions,
    });
  }, [inputs, data, analysis, assumptions, saveAnalysis]);

  const projections = dcf.projections || [];
  const chartData = [
    { year: 'Base', Revenue: assumptions.base_revenue, FCF: dcf.base_fcf },
    ...projections.map(p => ({ year: `Year ${p.year}`, Revenue: p.revenue, FCF: p.fcf, 'PV of FCF': p.pv_fcf })),
  ];

  if (!assumptions.base_revenue) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="dcf-page">
        Import data to view DCF valuation
      </div>
    );
  }

  const askingPrice = data?.seller_deal?.purchase_price || 449000;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dcf-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">DCF Valuation</h2>
          <p className="text-sm text-zinc-400 mt-1">Discounted Cash Flow analysis with adjustable assumptions</p>
        </div>
        <div className="flex items-center gap-2">
          <SectionPdfExport
            title="DCF Valuation"
            analysisName={analysis?.name}
            rows={projections}
            columns={buildColumns([
              { label: 'Year', key: 'year' },
              { label: 'Revenue', key: 'revenue', format: 'currency' },
              { label: 'Growth', key: 'growth_rate', format: 'percent' },
              { label: 'FCF', key: 'fcf', format: 'currency' },
              { label: 'PV of FCF', key: 'pv_fcf', format: 'currency' },
            ])}
          />
          <Button
            onClick={handleRecalculate}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="recalculate-dcf-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recalculate
          </Button>
        </div>
      </div>

      {/* Assumption Sliders */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">DCF Assumptions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SliderInput label="Discount Rate (WACC)" value={inputs.discount_rate} min={0.05} max={0.30} step={0.005}
              onChange={(v) => setInputs(p => ({ ...p, discount_rate: v }))} format={pct} testId="slider-discount-rate" />
            <SliderInput label="Revenue Growth Rate" value={inputs.growth_rate} min={-0.20} max={0.20} step={0.005}
              onChange={(v) => setInputs(p => ({ ...p, growth_rate: v }))} format={pct} testId="slider-growth-rate" />
            <ExitMultipleInput label="Exit Multiple" value={inputs.exit_multiple}
              onChange={(v) => setInputs(p => ({ ...p, exit_multiple: v }))} testId="input-exit-multiple" />
            <SliderInput label="COGS % of Revenue" value={inputs.cogs_pct} min={0.40} max={0.80} step={0.005}
              onChange={(v) => setInputs(p => ({ ...p, cogs_pct: v }))} format={pct} testId="slider-cogs" />
            <SliderInput label="OpEx % of Revenue" value={inputs.opex_pct} min={0.10} max={0.40} step={0.005}
              onChange={(v) => setInputs(p => ({ ...p, opex_pct: v }))} format={pct} testId="slider-opex" />
            <PercentInput label="CapEx %" value={inputs.capex_pct}
              onChange={(v) => setInputs(p => ({ ...p, capex_pct: v }))} testId="input-capex-pct" />
            <PercentInput label="Working Capital %" value={inputs.working_capital_pct}
              onChange={(v) => setInputs(p => ({ ...p, working_capital_pct: v }))} testId="input-wc-pct" />
            <TaxRateInput label="Tax Rate" value={inputs.tax_rate}
              onChange={(v) => setInputs(p => ({ ...p, tax_rate: v }))} testId="input-tax-rate" />
            <SliderInput label="Buyer Salary" value={inputs.buyer_salary} min={50000} max={150000} step={5000}
              onChange={(v) => setInputs(p => ({ ...p, buyer_salary: v }))} format={fmt} testId="slider-salary" />
          </div>
        </CardContent>
      </Card>

      {/* Valuation Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/60 border-indigo-500/20">
          <CardContent className="p-4">
            <span className="text-xs text-zinc-400">Enterprise Value (DCF)</span>
            <div className="font-mono text-2xl font-bold text-indigo-400 mt-1">{fmt(dcf.enterprise_value)}</div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-xs text-zinc-500">Implied {dcf.implied_multiple?.toFixed(2)}x SDE multiple</span>
              <Info className="w-3 h-3 text-zinc-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardContent className="p-4">
            <span className="text-xs text-zinc-400">PV of Future Cash Flows</span>
            <div className="font-mono text-2xl font-bold text-emerald-400 mt-1">{fmt(dcf.sum_pv_fcf)}</div>
            <span className="text-xs text-zinc-500">Years 1-5</span>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardContent className="p-4">
            <span className="text-xs text-zinc-400">PV of Terminal Value</span>
            <div className="font-mono text-2xl font-bold text-amber-400 mt-1">{fmt(dcf.pv_terminal)}</div>
            <span className="text-xs text-zinc-500">Year 5 FCF × {assumptions.exit_multiple || 3.0}x = {fmt(dcf.terminal_value)}</span>
          </CardContent>
        </Card>
      </div>

      {/* SDE Source Transparency */}
      <Card className={`bg-zinc-900/60 ${showSDEWarning ? 'border-amber-500/40' : 'border-zinc-800/60'}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-zinc-300">Implied SDE Multiple Calculation</CardTitle>
            {showSDEWarning && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-400 text-[10px]">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {sdeVariance.toFixed(0)}% Variance
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 rounded-lg bg-zinc-800/40 border border-indigo-500/20">
              <span className="text-[10px] text-indigo-400 uppercase tracking-wider">SDE Used for Multiple</span>
              <div className="font-mono text-lg font-semibold text-zinc-100 mt-1">{fmt(recalculatedSDE)}</div>
              <span className="text-[10px] text-zinc-500">Recalculated from P&L (2-Year Avg)</span>
            </div>
            <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Broker/Seller SDE</span>
              <div className="font-mono text-lg font-semibold text-zinc-300 mt-1">{fmt(brokerSDE)}</div>
              <span className="text-[10px] text-zinc-500">From Deal Tab (unverified)</span>
            </div>
            <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Implied Multiple</span>
              <div className="font-mono text-lg font-semibold text-zinc-100 mt-1">{dcf.implied_multiple?.toFixed(2)}x</div>
              <span className="text-[10px] text-zinc-500">EV ÷ Recalculated SDE</span>
            </div>
          </div>

          {showSDEWarning && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-300/90">
                <span className="font-medium">SDE Variance Warning:</span> The recalculated SDE differs from the broker's stated SDE by {sdeVariance.toFixed(0)}%. 
                This may indicate aggressive add-backs in the broker's SDE calculation. Review the SDE Calculation page to understand the difference.
              </div>
            </div>
          )}

          {!showSDEWarning && recalculatedSDE > 0 && brokerSDE > 0 && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-2">
              <Info className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-emerald-300/90">
                The broker's SDE and recalculated SDE are within 15% of each other, indicating the add-backs are generally reasonable.
              </div>
            </div>
          )}

          <div className="text-[10px] text-zinc-500 pt-1 border-t border-zinc-800/50">
            Note: The Implied SDE Multiple uses the recalculated SDE from P&L analysis (weighted 2-year average), not the broker's stated SDE. 
            This provides a more conservative and verified basis for valuation comparison.
          </div>
        </CardContent>
      </Card>

      {/* Valuation vs Price */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Valuation Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <CompareRow label="DCF Enterprise Value" value={dcf.enterprise_value} vs={askingPrice} />
            <CompareRow label="Seller Asking Price" value={askingPrice} vs={askingPrice} isRef />
            <CompareRow label="Buyer Planned Offer" value={data?.buyer_deal?.purchase_price} vs={askingPrice} />
            <CompareRow label="CabinetIQ Comparable" value={data?.comparable_deal?.purchase_price} vs={askingPrice} />
            <CompareRow label="Recommended Max (15% MoS)" value={dcf.enterprise_value ? dcf.enterprise_value * 0.85 : null} vs={askingPrice} />
          </div>
        </CardContent>
      </Card>

      {/* FCF Projections Chart */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Projected Cash Flows</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gradientFCF" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="year" tick={{ fill: '#71717a', fontSize: 11 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
              <Area type="monotone" dataKey="FCF" stroke="#6366f1" fill="url(#gradientFCF)" strokeWidth={2} />
              <Area type="monotone" dataKey="PV of FCF" stroke="#10b981" fill="transparent" strokeWidth={2} strokeDasharray="5 5" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Projections Table */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 text-xs">Year</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Growth</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Revenue</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Gross Profit</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">CapEx</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">NWC Change</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">FCF</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Discount</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">PV of FCF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projections.map((p) => (
                <TableRow key={p.year} className="border-zinc-800/40 hover:bg-zinc-800/20">
                  <TableCell className="font-mono text-xs text-zinc-200">Year {p.year}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-300">{pct(p.growth_rate)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(p.revenue)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(p.gross_profit)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-red-400">{fmt(p.capex ? -p.capex : 0)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-amber-400">{fmt(p.nwc_change || 0)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-emerald-400">{fmt(p.fcf)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-400">{p.discount_factor?.toFixed(4)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-indigo-400">{fmt(p.pv_fcf)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Buyer Cash Flow Waterfall */}
      <BuyerCashFlowWaterfall 
        projections={projections} 
        data={data} 
        results={results}
        buyerSalary={inputs.buyer_salary}
      />

      {/* ROI Metrics */}
      <BuyerROIMetrics
        projections={projections}
        data={data}
        results={results}
        buyerSalary={inputs.buyer_salary}
      />
    </div>
  );
}

function SliderInput({ label, value, min, max, step, onChange, format, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-400">{label}</span>
        <span className="font-mono text-sm text-zinc-200 font-medium">{format(value)}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
        className="w-full"
        data-testid={testId}
      />
    </div>
  );
}

function ExitMultipleInput({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-400">{label}</span>
      </div>
      <input
        type="number"
        step="0.1"
        min="0.5"
        max="10"
        value={value ?? 3.0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 3.0)}
        className="w-full bg-zinc-800/60 border-2 border-amber-500/30 rounded-md text-sm text-zinc-200 font-mono py-1.5 px-2.5
          focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/20
          hover:border-amber-500/50 transition-colors"
        data-testid={testId}
      />
      <span className="text-[10px] text-zinc-500 mt-1 block">Terminal Value = Year 5 FCF × Multiple</span>
    </div>
  );
}

function PercentInput({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-400">{label}</span>
        <span className="font-mono text-sm text-zinc-200 font-medium">{(value * 100).toFixed(1)}%</span>
      </div>
      <input
        type="number"
        step="0.005"
        min="0"
        max="1"
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full bg-zinc-800/60 border-2 border-amber-500/30 rounded-md text-sm text-zinc-200 font-mono py-1.5 px-2.5
          focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/20
          hover:border-amber-500/50 transition-colors"
        data-testid={testId}
      />
      <span className="text-[10px] text-zinc-500 mt-1 block">
        {label === 'CapEx %' ? 'CapEx = Revenue × CapEx%' : 'NWC = Revenue Increase × WC%'}
      </span>
    </div>
  );
}

function TaxRateInput({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-400">{label}</span>
        <span className="font-mono text-sm text-zinc-200 font-medium">{((value || 0) * 100).toFixed(0)}%</span>
      </div>
      <input
        type="number"
        step="0.01"
        min="0"
        max="0.50"
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full bg-zinc-800/60 border-2 border-amber-500/30 rounded-md text-sm text-zinc-200 font-mono py-1.5 px-2.5
          focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/20
          hover:border-amber-500/50 transition-colors"
        data-testid={testId}
      />
      <span className="text-[10px] text-zinc-500 mt-1 block">0 for S-Corp/LLC, 0.21 for C-Corp</span>
    </div>
  );
}

function CompareRow({ label, value, vs, isRef }) {
  if (value === undefined || value === null) return null;
  const diff = value - vs;
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800/40 last:border-0">
      <span className="text-xs text-zinc-400">{label}</span>
      <div className="flex items-center gap-4">
        <span className="font-mono text-sm text-zinc-200">{fmt(value)}</span>
        {!isRef && (
          <Badge variant="outline" className={`text-[10px] font-mono ${diff >= 0 ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
            {diff >= 0 ? '+' : ''}{fmt(diff)}
          </Badge>
        )}
      </div>
    </div>
  );
}


function BuyerCashFlowWaterfall({ projections, data, results, buyerSalary }) {
  const dealComp = results?.deal_comparison?.buyer || {};
  
  // Get debt structure from buyer deal
  const sbaLoan = dealComp.sba_loan || 0;
  const sbaYears = data?.buyer_deal?.sba_years || 10;
  const sbaDebtService = dealComp.sba_debt_service || 0;
  
  const sellerNote = dealComp.seller_note || 0;
  const sellerNoteYears = data?.buyer_deal?.seller_note_years || 5;
  const sellerDebtService = dealComp.seller_debt_service || 0;
  
  const equityInjection = dealComp.equity_injection || 0;
  const salary = buyerSalary || dealComp.buyer_salary || 75000;

  // Build waterfall data for Years 1-6+
  const waterfallData = [];
  let cumulativeCash = 0;
  let paybackYear = null;

  // Years 1-5 from DCF projections
  for (let i = 0; i < 5; i++) {
    const year = i + 1;
    const fcf = projections[i]?.fcf || 0;
    
    // SBA payment stops after sba_years
    const sbaPayment = year <= sbaYears ? sbaDebtService : 0;
    
    // Seller note payment stops after seller_note_years
    const sellerPayment = year <= sellerNoteYears ? sellerDebtService : 0;
    
    const cashAfterDebt = fcf - sbaPayment - sellerPayment;
    const totalCashToBuyer = cashAfterDebt + salary;
    cumulativeCash += totalCashToBuyer;
    
    // Check if equity recovered
    const recovered = cumulativeCash >= equityInjection && equityInjection > 0;
    if (recovered && !paybackYear) {
      paybackYear = year;
    }

    waterfallData.push({
      year: `Year ${year}`,
      yearNum: year,
      fcf,
      sbaPayment,
      sellerPayment,
      cashAfterDebt,
      buyerSalary: salary,
      totalCashToBuyer,
      cumulativeCash,
      recovered,
    });
  }

  // Year 6+ (steady state after seller note typically matures)
  const year6Fcf = projections[4]?.fcf || 0; // Use Year 5 FCF as proxy
  const year6SbaPayment = 6 <= sbaYears ? sbaDebtService : 0;
  const year6SellerPayment = 6 <= sellerNoteYears ? sellerDebtService : 0;
  const year6CashAfterDebt = year6Fcf - year6SbaPayment - year6SellerPayment;
  const year6TotalCash = year6CashAfterDebt + salary;
  cumulativeCash += year6TotalCash;
  
  waterfallData.push({
    year: 'Year 6+',
    yearNum: 6,
    fcf: year6Fcf,
    sbaPayment: year6SbaPayment,
    sellerPayment: year6SellerPayment,
    cashAfterDebt: year6CashAfterDebt,
    buyerSalary: salary,
    totalCashToBuyer: year6TotalCash,
    cumulativeCash,
    recovered: cumulativeCash >= equityInjection && equityInjection > 0,
    note: year6SellerPayment === 0 && sellerDebtService > 0 ? 'Seller note paid off' : null,
  });

  // Calculate simple payback period
  const year1TotalCash = waterfallData[0]?.totalCashToBuyer || 1;
  const paybackPeriod = equityInjection > 0 ? equityInjection / year1TotalCash : 0;

  // Check for all-cash deal
  const isAllCash = sbaLoan === 0 && sellerNote === 0;

  return (
    <Card className="bg-zinc-900/60 border-zinc-800/60">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Buyer Cash Flow Waterfall</CardTitle>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div>
              <span className="text-zinc-500">Equity Injection: </span>
              <span className="font-mono text-amber-400">{fmt(equityInjection)}</span>
            </div>
            <div>
              <span className="text-zinc-500">Simple Payback: </span>
              <span className="font-mono text-indigo-400">{paybackPeriod.toFixed(1)} years</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-1">Annual cash flow to buyer after debt service payments</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Waterfall Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 text-[10px]">Item</TableHead>
                {waterfallData.map((d) => (
                  <TableHead key={d.year} className="text-zinc-400 text-[10px] text-right">{d.year}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Row 1: DCF Free Cash Flow */}
              <TableRow className="border-zinc-800/40 hover:bg-zinc-800/20">
                <TableCell className="text-[10px] text-zinc-300">DCF Free Cash Flow</TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className="text-right font-mono text-[10px] text-emerald-400">{fmt(d.fcf)}</TableCell>
                ))}
              </TableRow>

              {/* Row 2: SBA Loan Payment */}
              <TableRow className="border-zinc-800/40 hover:bg-zinc-800/20">
                <TableCell className="text-[10px] text-zinc-300">
                  <span>(-) SBA Loan Payment</span>
                  {sbaLoan > 0 && <span className="text-zinc-500 ml-1">(10yr)</span>}
                </TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className={`text-right font-mono text-[10px] ${d.sbaPayment > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                    {d.sbaPayment > 0 ? fmt(-d.sbaPayment) : '$0'}
                  </TableCell>
                ))}
              </TableRow>

              {/* Row 3: Seller Note Payment */}
              <TableRow className="border-zinc-800/40 hover:bg-zinc-800/20">
                <TableCell className="text-[10px] text-zinc-300">
                  <span>(-) Seller Note Payment</span>
                  {sellerNote > 0 && <span className="text-zinc-500 ml-1">({sellerNoteYears}yr)</span>}
                </TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className={`text-right font-mono text-[10px] ${d.sellerPayment > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                    {d.sellerPayment > 0 ? fmt(-d.sellerPayment) : (d.note ? <span className="text-emerald-400">$0 ✓</span> : '$0')}
                  </TableCell>
                ))}
              </TableRow>

              {/* Row 4: Cash Flow After Debt Service */}
              <TableRow className="border-zinc-800/60 bg-zinc-800/20 hover:bg-zinc-800/30">
                <TableCell className="text-[10px] text-zinc-200 font-medium">= Cash After Debt Service</TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className={`text-right font-mono text-[10px] font-medium ${d.cashAfterDebt >= 0 ? 'text-zinc-100' : 'text-red-400'}`}>
                    {fmt(d.cashAfterDebt)}
                  </TableCell>
                ))}
              </TableRow>

              {/* Row 5: Buyer Salary */}
              <TableRow className="border-zinc-800/40 hover:bg-zinc-800/20">
                <TableCell className="text-[10px] text-zinc-300">(+) Buyer Salary</TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className="text-right font-mono text-[10px] text-indigo-400">{fmt(d.buyerSalary)}</TableCell>
                ))}
              </TableRow>

              {/* Row 6: Total Cash to Buyer */}
              <TableRow className="border-zinc-800/60 bg-emerald-500/10 hover:bg-emerald-500/15">
                <TableCell className="text-[10px] text-emerald-300 font-medium">= Total Cash to Buyer</TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className="text-right font-mono text-[10px] font-semibold text-emerald-400">{fmt(d.totalCashToBuyer)}</TableCell>
                ))}
              </TableRow>

              {/* Row 7: Cumulative Cash to Buyer */}
              <TableRow className="border-zinc-800/60 hover:bg-zinc-800/20">
                <TableCell className="text-[10px] text-zinc-300">Cumulative Cash to Buyer</TableCell>
                {waterfallData.map((d) => (
                  <TableCell key={d.year} className="text-right font-mono text-[10px] text-zinc-200">{fmt(d.cumulativeCash)}</TableCell>
                ))}
              </TableRow>

              {/* Row 8: Equity Payback Indicator */}
              {equityInjection > 0 && (
                <TableRow className="border-zinc-800/60 hover:bg-zinc-800/20">
                  <TableCell className="text-[10px] text-zinc-400">Equity Status</TableCell>
                  {waterfallData.map((d) => (
                    <TableCell key={d.year} className="text-right text-[10px]">
                      {d.recovered ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400">
                          <CheckCircle className="w-3 h-3" />
                          RECOVERED
                        </span>
                      ) : (
                        <span className="text-zinc-500">—</span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Summary Notes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-zinc-800/50">
          <div className="p-3 rounded-lg bg-zinc-800/40">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Equity Injection</span>
            <div className="font-mono text-lg font-semibold text-amber-400 mt-1">{fmt(equityInjection)}</div>
            <span className="text-[10px] text-zinc-500">Cash at closing</span>
          </div>
          <div className="p-3 rounded-lg bg-zinc-800/40">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Simple Payback Period</span>
            <div className="font-mono text-lg font-semibold text-indigo-400 mt-1">{paybackPeriod.toFixed(1)} years</div>
            <span className="text-[10px] text-zinc-500">Equity ÷ Year 1 Cash to Buyer</span>
          </div>
          <div className="p-3 rounded-lg bg-zinc-800/40">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Equity Recovered In</span>
            <div className="font-mono text-lg font-semibold text-emerald-400 mt-1">{paybackYear ? `Year ${paybackYear}` : '—'}</div>
            <span className="text-[10px] text-zinc-500">When cumulative cash ≥ equity</span>
          </div>
        </div>

        {/* Debt Structure Notes */}
        {isAllCash && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-emerald-300/90">
              <span className="font-medium">All-Cash Deal:</span> No debt financing detected. Cash After Debt Service equals DCF Free Cash Flow.
            </div>
          </div>
        )}

        {sellerNote > 0 && sellerNoteYears <= 5 && (
          <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-start gap-2">
            <Info className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-indigo-300/90">
              <span className="font-medium">Seller Note Maturity:</span> The {sellerNoteYears}-year seller note ({fmt(sellerNote)}) 
              will be fully amortized by Year {Math.ceil(sellerNoteYears)}. Year 6+ shows higher cash flow as the {fmt(sellerDebtService)}/year 
              payment stops.
            </div>
          </div>
        )}

        <div className="text-[10px] text-zinc-500 pt-2 border-t border-zinc-800/50">
          Note: Debt payments automatically stop after their respective loan terms (SBA: {sbaYears} years, Seller Note: {sellerNoteYears} years).
          Buyer Salary is added back as it represents cash compensation received by the owner-operator.
        </div>
      </CardContent>
    </Card>
  );
}


function BuyerROIMetrics({ projections, data, results, buyerSalary }) {
  const dealComp = results?.deal_comparison?.buyer || {};
  
  // Get debt structure
  const sbaDebtService = dealComp.sba_debt_service || 0;
  const sellerDebtService = dealComp.seller_debt_service || 0;
  const sellerNoteYears = data?.buyer_deal?.seller_note_years || 5;
  const sbaYears = data?.buyer_deal?.sba_years || 10;
  const equityInjection = dealComp.equity_injection || 0;
  const salary = buyerSalary || dealComp.buyer_salary || 75000;

  // Calculate Year 1 Total Cash to Buyer
  const year1Fcf = projections[0]?.fcf || 0;
  const year1SbaPayment = 1 <= sbaYears ? sbaDebtService : 0;
  const year1SellerPayment = 1 <= sellerNoteYears ? sellerDebtService : 0;
  const year1CashAfterDebt = year1Fcf - year1SbaPayment - year1SellerPayment;
  const year1TotalCash = year1CashAfterDebt + salary;

  // Calculate Cumulative Cash to Buyer through Year 5
  let cumulativeCashYear5 = 0;
  for (let i = 0; i < 5; i++) {
    const year = i + 1;
    const fcf = projections[i]?.fcf || 0;
    const sbaPayment = year <= sbaYears ? sbaDebtService : 0;
    const sellerPayment = year <= sellerNoteYears ? sellerDebtService : 0;
    const cashAfterDebt = fcf - sbaPayment - sellerPayment;
    const totalCash = cashAfterDebt + salary;
    cumulativeCashYear5 += totalCash;
  }

  // Calculate ROI Metrics
  const cashOnCashReturn = equityInjection > 0 ? (year1TotalCash / equityInjection) * 100 : null;
  const fiveYearReturn = equityInjection > 0 ? cumulativeCashYear5 / equityInjection : null;
  const paybackPeriod = year1TotalCash > 0 ? equityInjection / year1TotalCash : null;

  // Contextual ratings
  const getCashOnCashRating = (value) => {
    if (value === null) return { label: 'N/A', color: 'text-zinc-500', bg: 'bg-zinc-700/50' };
    if (value >= 25) return { label: 'Strong', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (value >= 15) return { label: 'Moderate', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { label: 'Weak', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const getFiveYearRating = (value) => {
    if (value === null) return { label: 'N/A', color: 'text-zinc-500', bg: 'bg-zinc-700/50' };
    if (value >= 2.5) return { label: 'Strong', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (value >= 1.5) return { label: 'Moderate', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { label: 'Weak', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const getPaybackRating = (value) => {
    if (value === null) return { label: 'N/A', color: 'text-zinc-500', bg: 'bg-zinc-700/50' };
    if (value <= 2) return { label: 'Strong', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (value <= 4) return { label: 'Moderate', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { label: 'Weak', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const cashOnCashRating = getCashOnCashRating(cashOnCashReturn);
  const fiveYearRating = getFiveYearRating(fiveYearReturn);
  const paybackRating = getPaybackRating(paybackPeriod);

  return (
    <Card className="bg-zinc-900/60 border-zinc-800/60">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-400" />
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Buyer ROI Metrics</CardTitle>
        </div>
        <p className="text-xs text-zinc-500 mt-1">Return on buyer's equity investment analysis</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cash-on-Cash Return (Year 1) */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Cash-on-Cash Return</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full ${cashOnCashRating.bg} ${cashOnCashRating.color}`}>
                {cashOnCashRating.label}
              </span>
            </div>
            <div className={`font-mono text-3xl font-bold ${cashOnCashRating.color}`}>
              {cashOnCashReturn !== null ? `${cashOnCashReturn.toFixed(1)}%` : 'N/A'}
            </div>
            <div className="mt-2 text-[10px] text-zinc-500">
              <div className="flex justify-between">
                <span>Year 1 Total Cash</span>
                <span className="font-mono text-zinc-400">{fmt(year1TotalCash)}</span>
              </div>
              <div className="flex justify-between">
                <span>÷ Equity Injection</span>
                <span className="font-mono text-zinc-400">{fmt(equityInjection)}</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-700/30 text-[9px] text-zinc-500">
              ≥25% Strong | 15-24% Moderate | &lt;15% Weak
            </div>
          </div>

          {/* 5-Year Total Return */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider">5-Year Total Return</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full ${fiveYearRating.bg} ${fiveYearRating.color}`}>
                {fiveYearRating.label}
              </span>
            </div>
            <div className={`font-mono text-3xl font-bold ${fiveYearRating.color}`}>
              {fiveYearReturn !== null ? `${fiveYearReturn.toFixed(2)}x` : 'N/A'}
            </div>
            <div className="mt-2 text-[10px] text-zinc-500">
              <div className="flex justify-between">
                <span>5-Year Cumulative Cash</span>
                <span className="font-mono text-zinc-400">{fmt(cumulativeCashYear5)}</span>
              </div>
              <div className="flex justify-between">
                <span>÷ Equity Injection</span>
                <span className="font-mono text-zinc-400">{fmt(equityInjection)}</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-700/30 text-[9px] text-zinc-500">
              ≥2.5x Strong | 1.5-2.4x Moderate | &lt;1.5x Weak
            </div>
          </div>

          {/* Payback Period */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Simple Payback Period</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full ${paybackRating.bg} ${paybackRating.color}`}>
                {paybackRating.label}
              </span>
            </div>
            <div className={`font-mono text-3xl font-bold ${paybackRating.color}`}>
              {paybackPeriod !== null ? `${paybackPeriod.toFixed(1)} yrs` : 'N/A'}
            </div>
            <div className="mt-2 text-[10px] text-zinc-500">
              <div className="flex justify-between">
                <span>Equity Injection</span>
                <span className="font-mono text-zinc-400">{fmt(equityInjection)}</span>
              </div>
              <div className="flex justify-between">
                <span>÷ Year 1 Total Cash</span>
                <span className="font-mono text-zinc-400">{fmt(year1TotalCash)}</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-700/30 text-[9px] text-zinc-500">
              ≤2yr Strong | 2-4yr Moderate | &gt;4yr Weak
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mt-4 text-[10px] text-zinc-500 pt-3 border-t border-zinc-800/50">
          <span className="text-zinc-400 font-medium">Note:</span> These metrics evaluate the deal from a return-on-investment perspective, 
          independent of enterprise value or asking price. Cash-on-Cash Return measures Year 1 yield on equity. 5-Year Total Return 
          shows cumulative return multiple. Simple Payback assumes constant Year 1 cash flow (actual payback may be faster as debt matures).
        </div>
      </CardContent>
    </Card>
  );
}
